// src/pages/platform/PlatformMembersPage.tsx
// Platform member management: list members, change roles, remove, and manage invitations.

import { useEffect, useState, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { observer } from '@react';
import { EntityAvatar } from '@/components/EntityAvatar';
import { usePlatform, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  Alert,
  Badge,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  ErrorAlert,
} from '@react/components';
import { Select } from '@/components/Select';

// ---------------------------------------------------------------------------
// Types & Constants
// ---------------------------------------------------------------------------

type MembersTab = 'members' | 'invitations';

const PLATFORM_ROLES = [
  'PLATFORM_ROLE_OWNER',
  'PLATFORM_ROLE_ADMIN',
  'PLATFORM_ROLE_MANAGER',
  'PLATFORM_ROLE_MEMBER',
  'PLATFORM_ROLE_VIEWER',
] as const;
type PlatformRole = (typeof PLATFORM_ROLES)[number];

const ASSIGNABLE_ROLES = PLATFORM_ROLES.filter((r) => r !== 'PLATFORM_ROLE_OWNER');

const ROLE_LABELS: Record<PlatformRole, string> = {
  PLATFORM_ROLE_OWNER: 'Owner',
  PLATFORM_ROLE_ADMIN: 'Admin',
  PLATFORM_ROLE_MANAGER: 'Manager',
  PLATFORM_ROLE_MEMBER: 'Member',
  PLATFORM_ROLE_VIEWER: 'Viewer',
};

const ROLE_BADGE_VARIANT: Record<PlatformRole, string> = {
  PLATFORM_ROLE_OWNER: 'error',
  PLATFORM_ROLE_ADMIN: 'warning',
  PLATFORM_ROLE_MANAGER: 'info',
  PLATFORM_ROLE_MEMBER: 'primary',
  PLATFORM_ROLE_VIEWER: 'secondary',
};

const MEMBER_STATUS_LABELS: Record<string, string> = {
  PLATFORM_MEMBER_STATUS_ACTIVE: 'Active',
  PLATFORM_MEMBER_STATUS_SUSPENDED: 'Suspended',
  PLATFORM_MEMBER_STATUS_INVITED: 'Invited',
  PLATFORM_MEMBER_STATUS_REMOVED: 'Removed',
};

const MEMBER_STATUS_VARIANT: Record<string, string> = {
  PLATFORM_MEMBER_STATUS_ACTIVE: 'success',
  PLATFORM_MEMBER_STATUS_SUSPENDED: 'error',
  PLATFORM_MEMBER_STATUS_INVITED: 'warning',
  PLATFORM_MEMBER_STATUS_REMOVED: 'secondary',
};

const INVITATION_STATUS_LABELS: Record<string, string> = {
  PLATFORM_INVITATION_STATUS_PENDING: 'Pending',
  PLATFORM_INVITATION_STATUS_ACCEPTED: 'Accepted',
  PLATFORM_INVITATION_STATUS_DECLINED: 'Declined',
  PLATFORM_INVITATION_STATUS_EXPIRED: 'Expired',
  PLATFORM_INVITATION_STATUS_REVOKED: 'Revoked',
};

const INVITATION_STATUS_VARIANT: Record<string, string> = {
  PLATFORM_INVITATION_STATUS_PENDING: 'warning',
  PLATFORM_INVITATION_STATUS_ACCEPTED: 'success',
  PLATFORM_INVITATION_STATUS_DECLINED: 'error',
  PLATFORM_INVITATION_STATUS_EXPIRED: 'secondary',
  PLATFORM_INVITATION_STATUS_REVOKED: 'secondary',
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function relativeTime(dateStr: string | undefined | null): string {
  if (!dateStr) return '--';
  try {
    const date = new Date(dateStr).getTime();
    const now = Date.now();
    const diffMs = now - date;
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHours = Math.floor(diffMin / 60);
    const diffDays = Math.floor(diffHours / 24);
    const diffWeeks = Math.floor(diffDays / 7);
    const diffMonths = Math.floor(diffDays / 30);
    const diffYears = Math.floor(diffDays / 365);

    if (diffSec < 60) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    if (diffWeeks < 5) return `${diffWeeks}w ago`;
    if (diffMonths < 12) return `${diffMonths}mo ago`;
    return `${diffYears}y ago`;
  } catch {
    return String(dateStr);
  }
}

function formatDate(dateStr: string | undefined | null): string {
  if (!dateStr) return '--';
  try {
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return String(dateStr);
  }
}

function normalizeRole(role: string | undefined): PlatformRole {
  if (!role) return 'PLATFORM_ROLE_MEMBER';
  if (PLATFORM_ROLES.includes(role as PlatformRole)) return role as PlatformRole;
  return 'PLATFORM_ROLE_MEMBER';
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const TABS: { id: MembersTab; label: string; labelKey: string }[] = [
  { id: 'members', label: 'Members', labelKey: 'platform.members' },
  { id: 'invitations', label: 'Invitations', labelKey: 'platform.invitations' },
];

/**
 * PlatformMembersPage - manage platform members and invitations.
 * Route: /platforms/:id/members
 */
export const PlatformMembersPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const store = usePlatform();
  const { t } = useI18n();

  // Tab state
  const [activeTab, setActiveTab] = useState<MembersTab>('members');

  // Members data
  const [members, setMembers] = useState<any[]>([]);
  const [invitations, setInvitations] = useState<any[]>([]);

  // Add member form
  const [showAddMember, setShowAddMember] = useState(false);
  const [addProfileId, setAddProfileId] = useState('');
  const [addRole, setAddRole] = useState<PlatformRole>('PLATFORM_ROLE_MEMBER');
  const [adding, setAdding] = useState(false);

  // Invite form
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<PlatformRole>('PLATFORM_ROLE_MEMBER');
  const [inviteMessage, setInviteMessage] = useState('');
  const [inviting, setInviting] = useState(false);

  // Role change state
  const [changingRoleId, setChangingRoleId] = useState<string | null>(null);
  const [newRole, setNewRole] = useState<PlatformRole>('PLATFORM_ROLE_MEMBER');

  // Remove confirm state
  const [removingId, setRemovingId] = useState<string | null>(null);

  // Cancel invitation confirm state
  const [cancellingId, setCancellingId] = useState<string | null>(null);

  // Success notifications
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // ---------------------------------------------------------------------------
  // Data loading
  // ---------------------------------------------------------------------------

  const loadMembers = useCallback(async () => {
    if (!id) return;
    try {
      const res = await store.ListPlatformMembers(id);
      const list = res?.items?.items;
      setMembers(Array.isArray(list) ? list : []);
    } catch {
      // Error handled by store
    }
  }, [id, store]);

  const loadInvitations = useCallback(async () => {
    if (!id) return;
    try {
      const res = await store.ListPlatformInvitations(id);
      const list = res?.items?.items;
      setInvitations(Array.isArray(list) ? list : []);
    } catch {
      // Error handled by store
    }
  }, [id, store]);

  useEffect(() => {
    loadMembers();
    loadInvitations();
  }, [loadMembers, loadInvitations]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const showSuccess = (msg: string) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  const handleAddMember = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !addProfileId.trim()) return;
    setAdding(true);
    try {
      await store.AddPlatformMember(id, {
        profile: { id: addProfileId.trim() } as any,
        role: addRole,
      });
      setAddProfileId('');
      setAddRole('PLATFORM_ROLE_MEMBER');
      setShowAddMember(false);
      showSuccess(t('platform.memberAdded'));
      loadMembers();
    } catch {
      // Error handled by store
    } finally {
      setAdding(false);
    }
  };

  const handleChangeRole = async (profileId: string) => {
    if (!id) return;
    try {
      await store.UpdatePlatformMemberRole(id, profileId, { role: newRole });
      setChangingRoleId(null);
      showSuccess(t('platform.roleUpdated'));
      loadMembers();
    } catch {
      // Error handled by store
    }
  };

  const handleRemoveMember = async (profileId: string) => {
    if (!id) return;
    try {
      await store.RemovePlatformMember(id, profileId);
      setRemovingId(null);
      showSuccess(t('platform.memberRemoved'));
      loadMembers();
    } catch {
      // Error handled by store
    }
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !inviteEmail.trim()) return;
    setInviting(true);
    try {
      await store.CreatePlatformInvitation(id, {
        invitee_email: inviteEmail.trim(),
        role: inviteRole,
        message: inviteMessage.trim() || undefined,
      });
      setInviteEmail('');
      setInviteRole('PLATFORM_ROLE_MEMBER');
      setInviteMessage('');
      setShowInvite(false);
      showSuccess(t('platform.invitationSent'));
      loadInvitations();
    } catch {
      // Error handled by store
    } finally {
      setInviting(false);
    }
  };

  const handleCancelInvitation = async (invitationId: string) => {
    if (!id) return;
    try {
      await store.CancelPlatformInvitation(id, invitationId);
      setCancellingId(null);
      showSuccess(t('platform.invitationCancelled'));
      loadInvitations();
    } catch {
      // Error handled by store
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="text-sm">
        <Link to="/platforms" className="text-muted-foreground hover:text-foreground">
          {t('platform.list')}
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to={`/platforms/${id}`} className="text-muted-foreground hover:text-foreground">
          {t('platform.details')}
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <span className="text-foreground">{t('platform.members')}</span>
      </nav>

      {/* The tab already names this page; the heading stays for screen readers. */}
      <h1 className="sr-only">{t('platform.members')}</h1>

      {/* Notifications */}
      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}
      {successMessage && (
        <Alert variant="success" dismissible onDismiss={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Tab navigation */}
      <div className="border-b border-border mb-6">
        <div className="flex gap-0 -mb-px">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
              }`}
            >
              {t(tab.labelKey)}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      {activeTab === 'members' && (
        <>
          {/* Members header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <p className="text-sm text-muted-foreground">
              {members.length} {members.length === 1 ? 'member' : 'members'}
            </p>
            <Button onClick={() => setShowAddMember(!showAddMember)}>
              {showAddMember ? t('common.cancel') : t('platform.addMember')}
            </Button>
          </div>

          {/* Add member form */}
          {showAddMember && (
            <Card>
              <CardHeader>
                <CardTitle>{t('platform.addMember')}</CardTitle>
                <CardDescription>{t('platform.addMemberDescription')}</CardDescription>
              </CardHeader>
              <form onSubmit={handleAddMember}>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="flex flex-col gap-1">
                      <label className="text-xs font-medium text-muted-foreground">
                        Profile ID
                      </label>
                      <Input
                        placeholder="Enter profile ID"
                        value={addProfileId}
                        onChange={(e) => setAddProfileId(e.target.value)}
                        required
                        fullWidth={false}
                        className="w-full"
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-xs font-medium text-muted-foreground">
                        {t('platform.role')}
                      </label>
                      <Select
                        value={addRole}
                        onChange={(e) => setAddRole(e.target.value as PlatformRole)} fullWidth={false}>
                        {ASSIGNABLE_ROLES.map((r) => (
                          <option key={r} value={r}>
                            {ROLE_LABELS[r]}
                          </option>
                        ))}
                      </Select>
                    </div>
                  </div>
                </CardContent>
                <CardFooter className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setShowAddMember(false)}
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button type="submit" disabled={adding || !addProfileId.trim()}>
                    {adding ? t('common.loading') : t('platform.addMember')}
                  </Button>
                </CardFooter>
              </form>
            </Card>
          )}

          {/* Members table */}
          <Card>
            <Table striped hoverable>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">{/* Avatar */}</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Joined</TableHead>
                  <TableHead>Last Active</TableHead>
                  <TableHead className="text-right">{t('common.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {store.isLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      {t('common.loading')}
                    </TableCell>
                  </TableRow>
                ) : members.length === 0 ? (
                  <TableEmpty colSpan={7} message={t('platform.noMembers')} />
                ) : (
                  members.map((member: any) => {
                    const profileId = member.profile?.id || member.entity?.id || '';
                    const displayName =
                      member.profile?.display_name ||
                      member.profile?.name ||
                      member.profile?.id ||
                      profileId ||
                      '--';
                    const role = normalizeRole(member.role);
                    const status = member.status || 'PLATFORM_MEMBER_STATUS_ACTIVE';
                    const isOwner = role === 'PLATFORM_ROLE_OWNER';
                    const isChangingRole = changingRoleId === profileId;
                    const isRemoving = removingId === profileId;

                    return (
                      <TableRow key={profileId || Math.random()}>
                        {/* Avatar */}
                        <TableCell>
                          <EntityAvatar name={displayName} resource={member.profile?.avatar} profileId={profileId} size="sm" className="h-9 w-9" />
                        </TableCell>

                        {/* User */}
                        <TableCell>
                          <div className="text-sm font-medium text-foreground">
                            {displayName}
                          </div>
                        </TableCell>

                        {/* Role */}
                        <TableCell>
                          {isChangingRole ? (
                            <div className="flex items-center gap-2">
                              <Select
                                value={newRole}
                                onChange={(e) => setNewRole(e.target.value as PlatformRole)} fullWidth={false}>
                                {ASSIGNABLE_ROLES.map((r) => (
                                  <option key={r} value={r}>
                                    {ROLE_LABELS[r]}
                                  </option>
                                ))}
                              </Select>
                              <Button
                                size="sm"
                                onClick={() => handleChangeRole(profileId)}
                              >
                                {t('common.save')}
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setChangingRoleId(null)}
                              >
                                {t('common.cancel')}
                              </Button>
                            </div>
                          ) : (
                            <Badge variant={ROLE_BADGE_VARIANT[role] as any} size="sm">
                              {ROLE_LABELS[role]}
                            </Badge>
                          )}
                        </TableCell>

                        {/* Status */}
                        <TableCell>
                          <Badge
                            variant={(MEMBER_STATUS_VARIANT[status] || 'secondary') as any}
                            size="sm"
                          >
                            {MEMBER_STATUS_LABELS[status] || status}
                          </Badge>
                        </TableCell>

                        {/* Joined */}
                        <TableCell>
                          <span className="text-sm text-muted-foreground">
                            {relativeTime(member.joined_at)}
                          </span>
                        </TableCell>

                        {/* Last Active */}
                        <TableCell>
                          <span className="text-sm text-muted-foreground">
                            {relativeTime(member.last_active_at)}
                          </span>
                        </TableCell>

                        {/* Actions */}
                        <TableCell className="text-right">
                          {isRemoving ? (
                            <div className="flex justify-end items-center gap-2">
                              <span className="text-xs text-destructive">
                                {t('platform.confirmRemove')}
                              </span>
                              <Button
                                size="sm"
                                variant="danger"
                                onClick={() => handleRemoveMember(profileId)}
                              >
                                {t('common.confirm')}
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setRemovingId(null)}
                              >
                                {t('common.cancel')}
                              </Button>
                            </div>
                          ) : (
                            <div className="flex justify-end gap-1">
                              {!isOwner ? (
                                <>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      setChangingRoleId(profileId);
                                      setNewRole(role);
                                    }}
                                  >
                                    {t('platform.changeRole')}
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-destructive"
                                    onClick={() => setRemovingId(profileId)}
                                  >
                                    {t('platform.remove')}
                                  </Button>
                                </>
                              ) : (
                                <span className="text-xs text-muted-foreground px-2 py-1">
                                  {t('platform.ownerProtected')}
                                </span>
                              )}
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </Card>
        </>
      )}

      {activeTab === 'invitations' && (
        <>
          {/* Invitations header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <p className="text-sm text-muted-foreground">
              {invitations.length} {invitations.length === 1 ? 'invitation' : 'invitations'}
            </p>
            <Button onClick={() => setShowInvite(!showInvite)}>
              {showInvite ? t('common.cancel') : t('platform.invite')}
            </Button>
          </div>

          {/* Invite form */}
          {showInvite && (
            <Card>
              <CardHeader>
                <CardTitle>{t('platform.invite')}</CardTitle>
                <CardDescription>{t('platform.inviteDescription')}</CardDescription>
              </CardHeader>
              <form onSubmit={handleInvite}>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="flex flex-col gap-1">
                      <label className="text-xs font-medium text-muted-foreground">
                        Email
                      </label>
                      <Input
                        type="email"
                        placeholder="user@example.com"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        required
                        fullWidth={false}
                        className="w-full"
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-xs font-medium text-muted-foreground">
                        {t('platform.role')}
                      </label>
                      <Select
                        value={inviteRole}
                        onChange={(e) => setInviteRole(e.target.value as PlatformRole)} fullWidth={false}>
                        {ASSIGNABLE_ROLES.map((r) => (
                          <option key={r} value={r}>
                            {ROLE_LABELS[r]}
                          </option>
                        ))}
                      </Select>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-muted-foreground">
                      {t('platform.inviteMessageLabel')}
                    </label>
                    <textarea
                      value={inviteMessage}
                      onChange={(e) => setInviteMessage(e.target.value)}
                      placeholder={t('platform.inviteMessagePlaceholder')}
                      rows={3}
                      className="rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                    />
                  </div>
                </CardContent>
                <CardFooter className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setShowInvite(false)}
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button type="submit" disabled={inviting || !inviteEmail.trim()}>
                    {inviting ? t('common.loading') : t('platform.sendInvitation')}
                  </Button>
                </CardFooter>
              </form>
            </Card>
          )}

          {/* Invitations table */}
          <Card>
            <Table striped hoverable>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Sent At</TableHead>
                  <TableHead>Expires At</TableHead>
                  <TableHead className="text-right">{t('common.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {store.isLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8">
                      {t('common.loading')}
                    </TableCell>
                  </TableRow>
                ) : invitations.length === 0 ? (
                  <TableEmpty colSpan={6} message={t('platform.noInvitations')} />
                ) : (
                  invitations.map((inv: any) => {
                    const invId = inv.entity?.id || inv.id || '';
                    const role = normalizeRole(inv.role);
                    const status =
                      inv.status || 'PLATFORM_INVITATION_STATUS_PENDING';
                    const isPending =
                      status === 'PLATFORM_INVITATION_STATUS_PENDING';
                    const isCancelling = cancellingId === invId;

                    return (
                      <TableRow key={invId || Math.random()}>
                        {/* Email */}
                        <TableCell>
                          <span className="text-sm text-foreground">
                            {inv.invitee_email || '--'}
                          </span>
                        </TableCell>

                        {/* Role */}
                        <TableCell>
                          <Badge variant={ROLE_BADGE_VARIANT[role] as any} size="sm">
                            {ROLE_LABELS[role]}
                          </Badge>
                        </TableCell>

                        {/* Status */}
                        <TableCell>
                          <Badge
                            variant={
                              (INVITATION_STATUS_VARIANT[status] || 'secondary') as any
                            }
                            size="sm"
                          >
                            {INVITATION_STATUS_LABELS[status] || status}
                          </Badge>
                        </TableCell>

                        {/* Sent At */}
                        <TableCell>
                          <span className="text-sm text-muted-foreground">
                            {formatDate(inv.sent_at)}
                          </span>
                        </TableCell>

                        {/* Expires At */}
                        <TableCell>
                          <span className="text-sm text-muted-foreground">
                            {formatDate(inv.expires_at)}
                          </span>
                        </TableCell>

                        {/* Actions */}
                        <TableCell className="text-right">
                          {isPending && (
                            <>
                              {isCancelling ? (
                                <div className="flex justify-end items-center gap-2">
                                  <span className="text-xs text-destructive">
                                    {t('platform.confirmCancel')}
                                  </span>
                                  <Button
                                    size="sm"
                                    variant="danger"
                                    onClick={() => handleCancelInvitation(invId)}
                                  >
                                    {t('common.confirm')}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => setCancellingId(null)}
                                  >
                                    {t('common.cancel')}
                                  </Button>
                                </div>
                              ) : (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-destructive"
                                  onClick={() => setCancellingId(invId)}
                                >
                                  {t('platform.cancelInvitation')}
                                </Button>
                              )}
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </Card>
        </>
      )}
    </div>
  );
});

export default PlatformMembersPage;
